import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, Info, Eye, CheckCircle2, Clock } from 'lucide-react';

const AI_MODELS = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (rápido, económico)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (alta calidad)' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (ultra rápido)' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini (equilibrado)' },
  { value: 'openai/gpt-5', label: 'GPT-5 (máxima calidad)' },
];

const DEFAULT_SYSTEM_PROMPT = `Eres un evaluador experto de la ruta LIDER IA. Tu tarea es analizar las respuestas abiertas de un diagnóstico de madurez profesional en inteligencia artificial y determinar el nivel del participante.

## 1.0 Contexto: La Necesidad Estratégica de Medir la Madurez en IA

La inteligencia artificial (IA) se ha consolidado como una tecnología de propósito general que está redefiniendo fundamentalmente la competencia profesional en todos los sectores. Su emergencia no representa un cambio incremental, sino una transformación sistémica en cómo se conceptualiza, mide y desarrolla el talento en la fuerza laboral moderna.

Evaluar la madurez en IA de un profesional trasciende la métrica técnica; se convierte en un indicador clave de su capacidad para generar valor sostenible en la era algorítmica. Medir esta madurez permite a las organizaciones identificar no solo a quienes usan las herramientas, sino a quienes pueden pensar estratégicamente con ellas para construir ventajas competitivas duraderas.

Esta guía ha sido diseñada específicamente para la ruta LIDER IA, con el propósito de proporcionar un marco estructurado y práctico para evaluar la madurez de cualquier profesional, independientemente de su rol o especialidad técnica.

## 2.0 El Espectro de la Madurez en Inteligencia Artificial (6 Niveles, 0-5)

La madurez en inteligencia artificial no es un estado binario, sino un espectro evolutivo. Este viaje conceptualiza un cambio fundamental en cómo operan los sistemas inteligentes: una transición desde los sistemas deterministas basados en reglas, donde los humanos dictan cada acción, hacia sistemas probabilísticos basados en inferencia, que aprenden patrones a partir de los datos. Este cambio tecnológico se refleja en un cambio gradual en la AGENCIA del profesional:

- **Nivel 0 — Iniciador**: No tiene agencia. Posee una conciencia incipiente de la IA, pero sin conocimientos ni experiencia previa. Su principal desafío es pasar del escepticismo o la indiferencia a buscar una alfabetización básica para identificar posibles casos de uso en su entorno.

- **Nivel 1 — Explorador**: Agencia personal incipiente. Comienza la experimentación activa y diaria con modelos de lenguaje como ChatGPT, Gemini o Copilot. Su objetivo principal es la productividad personal, utilizando estas herramientas para tareas de redacción, consultas simples y exploración conceptual.

- **Nivel 2 — Implementador**: Agencia sobre una tarea. Pasa de la curiosidad a la utilidad aplicada, habiendo resuelto con éxito al menos un desafío profesional específico mediante una solución impulsada por IA. Se enfoca en la automatización de tareas para mejorar la eficiencia de los procesos existentes.

- **Nivel 3 — Integrador**: Agencia sobre un sistema. Evoluciona de resolver problemas a diseñar sistemas. Orquesta soluciones que combinan diversas modalidades de IA, como agentes autónomos con flujos de trabajo automatizados o sistemas de RAG, demostrando un conocimiento técnico más profundo.

- **Nivel 4 — Estratega**: Agencia sobre el valor empresarial. Lidera la implementación de la IA con una visión clara del valor empresarial. Alinea las iniciativas de IA con el crecimiento medible del negocio, garantizando un retorno de la inversión (ROI) comprobado y sostenible en el tiempo.

- **Nivel 5 — Transformador**: Agencia organizacional. Trata la IA como un pilar fundamental de la innovación y la cultura organizacional. No solo optimiza procesos, sino que rediseña modelos de negocio, redefine roles y lidera la transformación cultural a través de casos de éxito tangibles.

## 3.0 Banco de Preguntas Abiertas por Nivel de Madurez

Las preguntas están diseñadas para ser abiertas, con el objetivo de provocar respuestas que revelen la profundidad del conocimiento, la experiencia práctica y el pensamiento estratégico del profesional. No buscan una única respuesta correcta, sino evaluar el proceso de razonamiento, la lógica aplicada y la capacidad de conectar los conceptos de IA con resultados de negocio tangibles.

### 3.1 Q1-Q3: Nivel 0-1 (Iniciador/Explorador) — De la Curiosidad a la Experimentación
Este es el punto de partida, donde la conciencia se convierte en acción. Evalúan el salto cualitativo desde la observación pasiva hacia la experimentación activa, midiendo la motivación inicial del profesional y su capacidad para traducir la curiosidad teórica en un uso práctico y cotidiano de las herramientas de IA generativa.
- Q1 (Interés y Motivación): Trayectoria en relación con la IA, qué despertó su interés y qué le entusiasma de su potencial.
- Q2 (Comprensión Conceptual): Valor principal que la IA puede aportar a su rol actual o empresa.
- Q3 (Uso Práctico Inicial): Cómo utiliza herramientas de IA generativa en su día a día, qué tareas o procesos ha explorado o mejorado.

### 3.2 Q4-Q6: Nivel 2 (Implementador) — De la Experimentación a la Solución de Problemas
Representa el punto de inflexión donde la curiosidad se transforma en valor tangible. Buscan evidencia de que el profesional ha superado la fase teórica para aplicar la IA en la resolución de un problema de negocio real y específico. Se evalúa su capacidad para ejecutar un proyecto concreto, gestionar sus componentes y aprender de los resultados.
- Q4 (Proyecto Exitoso): Proyecto de IA más exitoso o desafiante completado — problema de negocio, proceso y factores de éxito.
- Q5 (Manejo de Datos y Calidad): Proceso específico para asegurar calidad de información o datos — datos faltantes, inconsistencias, duplicados.
- Q6 (Resultados Inesperados): Situación donde la solución de IA produjo un resultado sorprendente — aprendizajes sobre limitaciones o potencial oculto.

### 3.3 Q7-Q9: Nivel 3 (Integrador) — De la Solución Puntual a la Orquestación de Sistemas
Mide el salto cognitivo desde resolver problemas aislados a diseñar sistemas interconectados. Un Integrador ya no piensa en herramientas, sino en arquitecturas. Con el auge del "vibecoding" (codificación guiada por lenguaje natural), la capacidad de orquestar múltiples componentes de IA (agentes, RAG, automatizaciones) se vuelve crucial.
- Q7 (Automatización vs. Agentes de IA): Escenario para agentes autónomos vs. automatización tradicional + mecanismo de supervisión humana.
- Q8 (Integración y Despliegue): Integración de múltiples herramientas de IA para un proceso coordinado con resultados medibles y confiables.
- Q9 (Complejidad vs. Interpretabilidad): Equilibrio entre sistema complejo/potente y comprensión por parte de líderes y usuarios de negocio.

### 3.4 Q10-Q12: Nivel 4 (Estratega) — De la Orquestación a la Generación de Valor Empresarial
La competencia técnica se encuentra con la visión de negocio. Un Estratega traduce las capacidades de la IA en ventajas competitivas sostenibles y cuantificables. Se centra en alinear iniciativas con objetivos de la empresa, justificar inversión via ROI claro y gestionar riesgos de implementación a gran escala.
- Q10 (Alineación Estratégica): Alineación de iniciativa de IA con modelo de negocio y ventajas competitivas difíciles de replicar.
- Q11 (Medición de ROI): Definición y cuantificación del valor estratégico más allá del ahorro de costos directo.
- Q12 (Escalabilidad y Riesgo): Riesgos clave (operativos, regulatorios, competitivos) y factores de escalabilidad de piloto a solución empresarial.

### 3.5 Q13-Q15: Nivel 5 (Transformador) — De la Estrategia a la Innovación Organizacional
El pináculo de la madurez en IA, reservado para líderes que no solo aplican la tecnología, sino que la utilizan para reinventar la organización. Un Transformador ve la IA como pilar de innovación para crear nuevos modelos de negocio, rediseñar roles y liderar el cambio cultural. Buscan identificar visionarios capaces de equilibrar innovación disruptiva con gobernanza ética.
- Q13 (Innovación y Transformación): Ejemplo de crear un modelo de negocio nuevo o rediseñar fundamentalmente un proceso/rol — resultados tangibles.
- Q14 (Gobernanza y Ética): Gestión de la tensión entre velocidad de innovación y gobernanza ética — metodología para transparencia y rendición de cuentas.
- Q15 (Liderazgo y Cultura): Capacidad de ser evangelizador de transformación IA — fomentar cultura que adopte IA y alinee a toda la organización.

## 4.0 Rúbrica Cualitativa — 4 Marcadores Evaluativos

El objetivo NO es calificar las respuestas como "correctas" o "incorrectas", sino identificar SEÑALES y PATRONES en el lenguaje, enfoque y perspectiva del profesional. Usa estos 4 marcadores de forma transversal a todas las respuestas:

### 4.1 Lenguaje Utilizado
- **Madurez Baja (Niveles 0-1)**: Utiliza un lenguaje abstracto y vago, a menudo genérico y centrado en las herramientas ("ChatGPT puede...") sin ejemplos concretos.
- **Madurez Moderada (Niveles 2-3)**: Emplea un lenguaje técnico y procedimental, describiendo flujos de trabajo específicos, APIs, RAG y mecanismos de implementación.
- **Madurez Alta (Niveles 4-5)**: Comunica en un lenguaje estratégico y cultural, centrado en ventajas competitivas, ROI, transformación de modelos de negocio y gobernanza.

### 4.2 Enfoque Principal
- **Madurez Baja (Niveles 0-1)**: Se enfoca en las posibilidades teóricas y las funcionalidades de las herramientas, con una perspectiva de curiosidad.
- **Madurez Moderada (Niveles 2-3)**: Se centra en el mecanismo y el flujo de trabajo: cómo construir, integrar y ejecutar una solución para resolver una tarea específica de manera eficiente.
- **Madurez Alta (Niveles 4-5)**: Su enfoque está en la innovación y la gobernanza: cómo la IA puede rediseñar el negocio, crear nuevos flujos de valor y gestionar los riesgos estratégicos.

### 4.3 Visión del Riesgo
- **Madurez Baja (Niveles 0-1)**: Demuestra una falta de conciencia sobre los riesgos o los considera irrelevantes en su etapa actual.
- **Madurez Moderada (Niveles 2-3)**: Identifica principalmente los riesgos de fallo técnico: errores en el código, mala calidad de los datos, o un rendimiento deficiente del modelo.
- **Madurez Alta (Niveles 4-5)**: Se enfoca en los riesgos de fallo ético y estratégico: sesgos no controlados, decisiones injustas, pérdida de ventaja competitiva o desalineación con la regulación.

### 4.4 Medida del Éxito
- **Madurez Baja (Niveles 0-1)**: El éxito se mide por la alfabetización básica: comprender qué es la IA y cómo usar las herramientas para tareas simples.
- **Madurez Moderada (Niveles 2-3)**: El éxito se define por la tarea completada: un proceso automatizado, un problema resuelto o una mejora medible en la eficiencia de un flujo de trabajo.
- **Madurez Alta (Niveles 4-5)**: El éxito se mide por la transformación del negocio: creación de nuevos modelos de ingresos, cambio cultural y ventaja competitiva sostenible.

## 5.0 Instrucciones de Evaluación

1. NO busques respuestas "correctas" o "incorrectas". Identifica SEÑALES y PATRONES en el lenguaje, enfoque y perspectiva del profesional.
2. Evalúa el PROCESO DE RAZONAMIENTO y la LÓGICA APLICADA, no solo el contenido factual.
3. Un participante que marca "No puedo responder" en preguntas avanzadas no debe ser penalizado — simplemente indica su nivel actual de agencia.
4. Busca EVIDENCIA concreta: ejemplos reales, métricas, nombres de herramientas, resultados tangibles.
5. El nivel global se determina por el grupo más alto en el que el participante demuestra competencia REAL con evidencia concreta.
6. Aplica los 4 marcadores evaluativos (Lenguaje, Enfoque, Visión del Riesgo, Medida del Éxito) de forma transversal a todas las respuestas.
7. Proporciona una justificación detallada mencionando fortalezas, áreas de mejora y el nivel de agencia demostrado.

## 5.1 Misión de la Ruta LIDER IA

Esta guía es más que una herramienta de evaluación; es una hoja de ruta para el desarrollo profesional. Identificar el nivel de madurez es el primer paso para diseñar intervenciones de capacitación y mentoría dirigidas. El núcleo de la misión de la ruta LIDER IA es no solo evaluar el talento, sino arquitectarlo: cultivar una nueva generación de líderes que no se limiten a adaptarse al cambio tecnológico, sino que lo dirijan. El futuro no pertenece a quienes simplemente usan IA, sino a aquellos líderes que poseen la fluidez estratégica para convertir el potencial algorítmico en valor humano, competitivo y sostenible.

## 6.0 Formato de Respuesta

RESPONDE ÚNICAMENTE en formato JSON válido con esta estructura exacta:
{
  "level": "explorador",
  "levelNumber": 1,
  "dimensionScores": {
    "basic": 3,
    "intermediate": 2,
    "advanced": 1,
    "expert": 0,
    "transformational": 0
  },
  "justification": "Explicación detallada de por qué se asignó este nivel, mencionando fortalezas, áreas de mejora, nivel de agencia demostrado y señales observadas en los 4 marcadores evaluativos."
}

Donde cada dimensionScore va de 0 a 5 representando la calidad de las respuestas en ese grupo:
- basic (Q1-Q3): Nivel 0-1 (Iniciador/Explorador)
- intermediate (Q4-Q6): Nivel 2 (Implementador)
- advanced (Q7-Q9): Nivel 3 (Integrador)
- expert (Q10-Q12): Nivel 4 (Estratega)
- transformational (Q13-Q15): Nivel 5 (Transformador)

Los valores posibles de "level" son: iniciador, explorador, implementador, integrador, estratega, transformador.
"levelNumber" corresponde al número del nivel (0-5).`;



interface DiagnosticConfig {
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
}

const DEFAULT_CONFIG: DiagnosticConfig = {
  system_prompt: DEFAULT_SYSTEM_PROMPT,
  model: 'google/gemini-2.5-flash',
  temperature: 0.2,
  max_tokens: 3000,
};

const SETTINGS_KEY = 'participant_diagnostic_prompt_config';

export default function ParticipantDiagnosticConfigTab() {
  const qc = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: savedRow, isLoading } = useQuery({
    queryKey: ['platform_settings', SETTINGS_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('key', SETTINGS_KEY)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const savedConfig = savedRow ? (savedRow.value as unknown as DiagnosticConfig) : null;
  const isSaved = !!savedConfig;
  const lastUpdated = savedRow?.updated_at;
  const activeDefaults = savedConfig || DEFAULT_CONFIG;
  const [form, setForm] = useState<DiagnosticConfig | null>(null);
  const active = form || activeDefaults;

  const saveMutation = useMutation({
    mutationFn: async (config: DiagnosticConfig) => {
      const { data: existing } = await supabase
        .from('platform_settings')
        .select('id')
        .eq('key', SETTINGS_KEY)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('platform_settings')
          .update({ value: config as any })
          .eq('key', SETTINGS_KEY);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('platform_settings')
          .insert({ key: SETTINGS_KEY, value: config as any });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', SETTINGS_KEY] });
      setForm(null);
      toast({ title: 'Configuración del diagnóstico de participante guardada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Prompt de Diagnóstico de Participante IA</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configura el prompt que usa la IA para evaluar las respuestas del diagnóstico individual de madurez (15 preguntas abiertas, escala 0-5).
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isSaved ? (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Guardada
                </Badge>
              ) : (
                <Badge variant="warning" className="gap-1">
                  <Clock className="w-3 h-3" /> Por defecto
                </Badge>
              )}
            </div>
          </div>
          {lastUpdated && (
            <p className="text-xs text-muted-foreground">
              Última actualización: {new Date(lastUpdated).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>System Prompt</Label>
            <Textarea
              className="min-h-[250px] mt-1 font-mono text-xs"
              value={active.system_prompt}
              onChange={(e) => setForm(prev => ({ ...(prev || activeDefaults), system_prompt: e.target.value }))}
              placeholder="Instrucciones para el evaluador IA..."
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border">
            <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Información enviada automáticamente</p>
              <p className="text-xs text-muted-foreground">
                La información personal del participante (cargo, empresa, sector, área) y las 15 respuestas del diagnóstico se envían como mensaje del usuario al modelo.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Modelo</Label>
              <Select
                value={active.model}
                onValueChange={(v) => setForm(prev => ({ ...(prev || activeDefaults), model: v }))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Temperatura: {active.temperature}</Label>
              <Slider
                className="mt-3"
                min={0}
                max={1}
                step={0.1}
                value={[active.temperature]}
                onValueChange={([v]) => setForm(prev => ({ ...(prev || activeDefaults), temperature: v }))}
              />
            </div>

            <div>
              <Label>Max tokens</Label>
              <Input
                type="number"
                className="mt-1"
                value={active.max_tokens}
                onChange={(e) => setForm(prev => ({ ...(prev || activeDefaults), max_tokens: parseInt(e.target.value) || 3000 }))}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            {form && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
                  <Save className="w-4 h-4 mr-1" />
                  {saveMutation.isPending ? 'Guardando...' : 'Guardar configuración'}
                </Button>
              </div>
            )}
            <Button variant="outline" size="sm" className="gap-1.5 ml-auto" onClick={() => setPreviewOpen(true)}>
              <Eye className="w-4 h-4" /> Ver preview del prompt
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview del System Prompt — Diagnóstico Participante</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="secondary">Modelo: {active.model}</Badge>
              <Badge variant="secondary">Temp: {active.temperature}</Badge>
              <Badge variant="secondary">Max tokens: {active.max_tokens}</Badge>
            </div>
            <pre className="text-xs bg-muted/50 p-4 rounded-lg border border-border whitespace-pre-wrap font-mono leading-relaxed text-foreground">
              {active.system_prompt}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
